import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAppStore } from "@/stores/app";

// ── Standardised API response envelope ────────────────────────────────────────
export interface ApiEnvelope<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  code?: string;
  requestId?: string;
}

const api = axios.create({
  // Priority: VITE_API_BASE_URL (already includes /api/v1, used in prod)
  //           VITE_API_URL (legacy — appends /api/v1)
  //           Relative /api/v1 (dev server Vite proxy)
  baseURL: import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api/v1`
      : "/api/v1",
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

// ── Retry configuration for network failures ──────────────────────────────────
const RETRY_MAX = 3;
const RETRY_BASE_MS = 500; // 500 → 1000 → 2000 (exponential backoff)

/** Retryable conditions: network errors, 502/503/504, or ECONNABORTED. */
function isRetryable(error: AxiosError): boolean {
  if (!error.response) return true; // network error / timeout
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
}

async function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ── Request interceptor: attach JWT + unique request ID ──────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Tag every request so logs can be correlated
  config.headers["X-Request-Id"] =
    crypto.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return config;
});

// ── Response interceptor: refresh token on 401 ───────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (v: string) => void;
  reject: (e: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        localStorage.clear();
        window.location.href = "/auth/login";
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post<{
          data: { accessToken: string; refreshToken: string };
        }>("/api/v1/auth/refresh", { refreshToken });
        const { accessToken, refreshToken: newRefresh } = data.data;

        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("refresh_token", newRefresh);
        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = "/auth/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ── Response interceptor: extract requestId + standardise envelope ────────────
// Also enriches error objects so handlers can surface requestId in the UI.
api.interceptors.response.use(
  (res) => {
    // Attach requestId from response header or body for observability
    const rid = res.headers["x-request-id"] ?? res.data?.requestId;
    if (rid) (res as any)._requestId = rid;
    return res;
  },
  (error: AxiosError) => {
    // Enrich the error with requestId so error displays can show/copy it
    const requestId =
      error.response?.headers?.["x-request-id"] ??
      (error.response?.data as any)?.requestId ??
      "unknown";
    const code = (error.response?.data as any)?.code;
    const message =
      (error.response?.data as any)?.error ??
      (error.response?.data as any)?.message ??
      error.message;
    // Attach to error object for downstream consumers (error handler, components)
    (error as any).requestId = requestId;
    (error as any).code = code;
    (error as any).message = message;
    return Promise.reject(error);
  },
);

// ── 429 Rate-limit handler ────────────────────────────────────────────────────
api.interceptors.response.use(undefined, (error: AxiosError) => {
  if (error.response?.status === 429) {
    const retryAfter = (error.response.data as any)?.retryAfter ?? 60;
    // Fire a custom DOM event so any toast listener can react globally
    window.dispatchEvent(
      new CustomEvent("api:rate-limited", { detail: { retryAfter } }),
    );
  }
  return Promise.reject(error);
});

// ── 503 / maintenance handler ─────────────────────────────────────────────────
api.interceptors.response.use(
  (res) => {
    try {
      useAppStore().maintenance = false;
    } catch {}
    return res;
  },
  (error: AxiosError) => {
    if (error.response?.status === 503) {
      try {
        useAppStore().maintenance = true;
      } catch {}
    }
    return Promise.reject(error);
  },
);

// ── Retry interceptor: exponential backoff for transient failures ─────────────
api.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as InternalAxiosRequestConfig & {
    _retryCount?: number;
  };
  if (!config) return Promise.reject(error);
  config._retryCount = config._retryCount ?? 0;

  if (config._retryCount < RETRY_MAX && isRetryable(error)) {
    config._retryCount += 1;
    // Equal-jitter exponential backoff: [cap/2, cap] where cap = min(30s, base × 2^attempt)
    // Prevents all clients from retrying at the same instant after a shared outage.
    const cap = Math.min(
      30_000,
      RETRY_BASE_MS * Math.pow(2, config._retryCount),
    );
    const delay = Math.round(cap / 2 + Math.random() * (cap / 2));
    await sleep(delay);
    return api(config);
  }

  return Promise.reject(error);
});

export default api;
